// MUI Imports
// Redux Imports
import { LinearProgress } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
// React Router Imports
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ConfirmProvider } from "material-ui-confirm";
import React, { Suspense, useEffect } from "react";
//import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Router } from "react-router-dom";

import { light as theme } from "./theme";
import CrossSkillingDashboard from "./ts/components/cross-skilling-dashboard";
import MiniDrawer from "./ts/components/drawer";
import FullTimeMiniDrawer from "./ts/components/full-time-scheduler";
import AdminConfiguration from "./ts/components/gat-admin";
import GlobalSnackBar from "./ts/components/GlobalSnackBar";
import TimeOffBalancesReport from "./ts/components/time-off-balances-report";
import WorkerActivity from "./ts/components/worker-activity";
import { defaultConfirmOptions } from "./ts/config/confirm-config";
import {
	CROSS_SKILLING_PERMISSIONS,
	EVALUATION_PERMISSIONS,
	EVALUATION_SETUP_PERMISSIONS,
	SETTINGS_PERMISSIONS,
} from "./ts/config/permissions";
import DeepLink from "./ts/features/DeepLink/DeepLink";
import Login from "./ts/features/user/login/login";
import { syncUserAppInfo } from "./ts/features/user/util/app-info";
import WorkerSnackBar from "./ts/features/web-worker-app/Components/WorkerSnackBar";
import WorkersLogin from "./ts/features/web-worker-app/Login/WorkersLogin";
import AuthRoute from "./ts/utils/auth-route";
import DetectHome from "./ts/utils/detect-home";
import i18nFileGenerator from "./ts/utils/i18n-file-generator";
import PrivateRoute from "./ts/utils/private-route";
import RequirePermission from "./ts/utils/require-permission";
import withScrollToTop from "./ts/utils/scroll-to-top"; // Import HOC

const FactoryConfigCompaniesSettings = React.lazy(() =>
	import("./js/components/gat-admin/factory-config-companies-settings")
);
const Dashboard = React.lazy(() =>
	import("./ts/components/attendance-reports")
);
const AbsenceReports = React.lazy(() =>
	import("./ts/components/absence-reports")
);
const AdminConfigSettings = React.lazy(() =>
	import(
		"./ts/components/gat-admin/admin-config-companies-settings/admin-config-settings"
	)
);
const InsightDashboard = React.lazy(() =>
	import("./ts/components/insight-dashboard")
);
const Jobs = React.lazy(() => import("./ts/components/jobs"));
const Skills = React.lazy(() => import("./ts/components/skills"));
const Workcenters = React.lazy(() => import("./ts/components/workcenters"));
const Workers = React.lazy(() => import("./ts/components/workers"));
const Users = React.lazy(() => import("./ts/components/users"));
const MinHours = React.lazy(() => import("./ts/components/min-hours"));

const Certification = React.lazy(() => import("./ts/features/certifications"));
const SkillMatrix = React.lazy(() => import("./ts/features/skill-matrix"));
const ForgotPassword = React.lazy(() =>
	import("./ts/features/user/forgot-password")
);
const ResetPassword = React.lazy(() =>
	import("./ts/features/user/reset-password")
);
const Form = React.lazy(() =>
	import("./ts/features/web-worker-app/Components/Form")
);

const RegisterHere = React.lazy(() =>
	import("./ts/features/web-worker-app/Components/RegisterHere")
);
const DownloadApp = React.lazy(() =>
	import("./ts/features/web-worker-app/DownloadApp")
);
const Settings = React.lazy(() =>
	import("./ts/features/web-worker-app/Settings")
);
const WorkerProfile = React.lazy(() =>
	import("./ts/features/web-worker-app/WorkerProfile")
);
const WorkerProfileDetails = React.lazy(() =>
	import("./ts/features/web-worker-app/WorkerProfileDetails")
);
const WorkerJobsSkills = React.lazy(() =>
	import("./ts/features/web-worker-app/jobs-skills/jobs-skills-page")
);
const WorkerOvertime = React.lazy(() =>
	import("./ts/features/web-worker-app/overtime/overtime-page")
);
const WorkerTimeOff = React.lazy(() =>
	import("./ts/features/web-worker-app/time-off/time-off-overview-page")
);
const WorkerNotifications = React.lazy(() =>
	import("./ts/features/web-worker-app/notifications/notifications-page")
);
const WorkerQuietDays = React.lazy(() =>
	import("./ts/features/web-worker-app/quiet-days/quiet-days-page")
);
const WorkerShell = React.lazy(() =>
	import("./ts/features/web-worker-app/WorkerShell")
);
const WorkersMain = React.lazy(() =>
	import("./ts/features/web-worker-app/WorkersMain")
);
const WorkerEvaluationsPage = React.lazy(() =>
	import("./ts/features/web-worker-app/worker-evaluations-page")
);
const ShiftAcknowledgement = React.lazy(() =>
	import("./ts/features/web-worker-app/shift-acknowledgement")
);
const NotFound = React.lazy(() => import("./ts/components/NotFound"));
const ShiftSwaps = React.lazy(() => import("./ts/features/worker-requests"));
const RegularShiftRequestsPage = React.lazy(() => import("./ts/features/worker-requests/regular-shift-requests-page"));
const FlexShiftRequestsPage = React.lazy(() => import("./ts/features/worker-requests/flex-shift-requests-page"));

const DemoSetup = React.lazy(() => import("./ts/components/demo-setup"));
const Evaluations = React.lazy(() => import("./ts/components/evaluations"));
const EvaluationPrint = React.lazy(() =>
	import("./ts/components/evaluations/ui/evaluation-print")
);
const SkillEvaluation = React.lazy(() => import("./ts/components/evaluation-setup"));

const themeConstructed = createTheme(theme);

function App() {
	const authenticated = useSelector((state) => state.user.authenticated);
	const workerAuthenticated = useSelector(
		(state) => state.worker.authenticated
	);

	// Fire-and-forget supervisor app-info analytics: on app open, whenever the
	// supervisor becomes authenticated (login/refresh), and when the tab returns
	// to the foreground. Safely no-ops without a supervisor JWT.
	useEffect(() => {
		syncUserAppInfo();
		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				syncUserAppInfo();
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, [authenticated]);

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={themeConstructed}>
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					{/* REMOVE HELMET and CHATSCRIPT */}
					{/* <Helmet>
						<script
							type='text/javascript'
							id='hs-script-loader'
							async
							defer
							src='//js.hs-scripts.com/21474340.js'
						/>
					</Helmet> */}
					<div className='App'>
						<ConfirmProvider defaultOptions={defaultConfirmOptions}>
							<Suspense fallback={<LinearProgress />}>
							<BrowserRouter
								future={{
									v7_relativeSplatPath: true,
									v7_startTransition: true,
								}}
							>
								<Routes>
									<Route
										element={
											<AuthRoute
												authenticated={authenticated}
												workerApp={false}
											/>
										}
									>
										<Route path='/login' element={<Login />} />
										<Route
											path='/forgot-password'
											element={<ForgotPassword />}
										/>
										<Route path='/verifyEmail' element={<ResetPassword />} />
									</Route>

									<Route
										element={
											<PrivateRoute
												authenticated={authenticated}
												workerApp={false}
											/>
										}
									>
										<Route path='/' element={<DetectHome />} />
										<Route path='/flex-schedule' element={<MiniDrawer />} />
										<Route path='/shift' element={<MiniDrawer />} />
										<Route
											element={
												<RequirePermission
													permission={SETTINGS_PERMISSIONS.WORKERS.VIEW}
												/>
											}
										>
											<Route path='/manage-workers' element={<Workers />} />
										</Route>
										<Route
											element={
												<RequirePermission
													permission={SETTINGS_PERMISSIONS.USERS.VIEW}
												/>
											}
										>
											<Route path='/manage-users' element={<Users />} />
										</Route>
										<Route
											element={
												<RequirePermission
													permission={SETTINGS_PERMISSIONS.SKILLS.VIEW}
													flag='showCrossSkilling'
												/>
											}
										>
											<Route path='/manage-skills' element={<Skills />} />
										</Route>
										<Route
											element={
												<RequirePermission
													permission={SETTINGS_PERMISSIONS.JOB_ROLES.VIEW}
												/>
											}
										>
											<Route path='/manage-jobs' element={<Jobs />} />
										</Route>
										<Route
											element={
												<RequirePermission
													permission={SETTINGS_PERMISSIONS.WORKCENTERS.VIEW}
												/>
											}
										>
											<Route path='/workcenters' element={<Workcenters />} />
										</Route>
										<Route path='/reports' element={<Dashboard />} />
										<Route
											path='/absence-reports'
											element={<AbsenceReports />}
										/>
										<Route
											path='/min-hours'
											element={<MinHours />}
										/>
										<Route
											path='/worker-activity'
											element={<WorkerActivity />}
										/>
										<Route
											path='/time-off-balance-report'
											element={<TimeOffBalancesReport/>}
										/>
                    <Route
                      path='/time-off-balance'
                      element={<TimeOffBalancesReport/>}
                    />
										<Route
											path='/admin-configuration'
											element={<AdminConfiguration />}
										/>
										<Route
											element={
												<RequirePermission
													permission={CROSS_SKILLING_PERMISSIONS.VIEW_SUMMARY}
												/>
											}
										>
											<Route path='/insight' element={<InsightDashboard />} />
										</Route>
										<Route
											element={
												<RequirePermission
													permission={SETTINGS_PERMISSIONS.CERTIFICATIONS.VIEW}
													flag='certificationsEnabled'
												/>
											}
										>
											<Route
												path='/certifications'
												element={<Certification />}
											/>
										</Route>
										<Route
											path='/admin-settings'
											element={<AdminConfigSettings />}
										/>
										<Route path='/day-report' element={<Dashboard />} />
										<Route path='/skills-matrix' element={<SkillMatrix />} />
										<Route
											path='/cross-skilling'
											element={<CrossSkillingDashboard />}
										/>
										<Route
											path='/full-time-schedule'
											element={<FullTimeMiniDrawer />}
										/>
										{/* <Route
											path='/full-time-schedule'
											element={<WeeklySchedule />}
										/> */}
										{/*<Route path='/worker-requests' element={<ShiftSwaps />} />*/}
									<Route path='/worker-requests' element={<RegularShiftRequestsPage />} />
									<Route path='/worker-requests-flex' element={<FlexShiftRequestsPage />} />
										<Route path='/evaluations' element={<Evaluations />} />
										<Route
											element={
												<RequirePermission
													permission={EVALUATION_PERMISSIONS.WORKERS.FORM.VIEW}
												/>
											}
										>
											<Route
												path='/evaluations/print'
												element={<EvaluationPrint />}
											/>
										</Route>
										<Route
											element={
												<RequirePermission
													permission={EVALUATION_SETUP_PERMISSIONS.VIEW}
													flag='EnableSkillEvaluationForm'
												/>
											}
										>
											<Route
												path='/evaluation-setup'
												element={<SkillEvaluation />}
											/>
										</Route>
									</Route>

									{/* Only load route for demo reset for dev and demo branch */}
									{(process.env.REACT_APP_ENV === "demo" ||
										process.env.REACT_APP_ENV === "dev") && [
										<Route
											key={"factory-settings"}
											path='/factory-settings'
											element={<FactoryConfigCompaniesSettings />}
										/>,
										<Route
											key={"demo-setup"}
											path='/demo'
											element={<DemoSetup />}
										/>,
									]}
									{/* For dev use only */}
									{process.env.REACT_APP_ENV === "dev" && (
										<Route
											path='/i18n-file-generator'
											element={<i18nFileGenerator />}
										/>
									)}
									<Route path='/wl' element={<WorkersLogin />} />
									<Route path='/dl/:type?/:id?' element={<DeepLink />} />
									<Route path='/register-here' element={<RegisterHere />} />
									<Route path='/help-support' element={<Form />} />
									<Route
										element={
											<PrivateRoute
												authenticated={workerAuthenticated}
												workerApp={true}
											/>
										}
									>
										<Route element={<WorkerShell />}>
											<Route
												path='/w'
												element={<Navigate to='/wd' replace />}
											/>
										<Route path='/wd' element={<WorkersMain />} />
										<Route path='/we' element={<WorkerEvaluationsPage />} />
										<Route path='/ws' element={<Settings />} />
										<Route path='/wqd' element={<WorkerQuietDays />} />
										<Route path='/wp' element={<WorkerProfile />} />
										<Route
											path='/wpd'
											element={<WorkerProfileDetails />}
										/>
										<Route path='/wjs' element={<WorkerJobsSkills />} />
											<Route path='/wot' element={<WorkerOvertime />} />
											<Route path='/wto' element={<WorkerTimeOff />} />
											<Route path='/wn' element={<WorkerNotifications />} />
											<Route path='/wa' element={<ShiftAcknowledgement />} />
										<Route path='/download-app' element={<DownloadApp />} />
											</Route>
									</Route>
									<Route path='/:page?' element={<NotFound />} />
								</Routes>
							</BrowserRouter>
						</Suspense>
						</ConfirmProvider>
						<WorkerSnackBar />
						<GlobalSnackBar />
					</div>
				</LocalizationProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	);
}

export default App;
